import React, { useState, useEffect } from 'react';
import {
  Typography, Box, Button, Stack, TextField, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, IconButton, Tooltip, Paper, Chip
} from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import {
  Add as AddIcon,
  FileDownload as FileDownloadIcon,
  Edit as EditIcon,
  AttachMoney as AttachMoneyIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Download as DownloadIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { exportToExcel, exportToPrintWindow, exportStaffDetailsToPrint } from '../utils/exportUtils';
import { useToast } from '../context/ToastContext';
import DynamicDataGrid from '../components/DynamicDataGrid';
import { deleteStaff, archiveStaff } from '../slices/staffSlice';
import {
  fetchEmployeeTypes,
} from '../slices/employeeTypesSlice';


const MotionPaper = motion(Paper);

const StaffPage = ({ isArchivedView = false}) => {
  
  const dispatch = useDispatch();
  const { filtered } = useSelector((state) => state.staff);
  const navigate = useNavigate();
  const staff = useSelector((state) => state.staff.filtered);
  const [filterValues, setFilterValues] = useState({
    first_name: '',
    last_name: '',
    department: '',
    status: ''
  });

  useEffect(() => {
    dispatch(fetchEmployeeTypes());
  }, [dispatch]);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
    const { showToast } = useToast();
  const { items: employeeTypes } = useSelector((state) => state.employeeTypes);

  const filteredStaff = staff.filter((row) =>
    row.is_archived === isArchivedView &&
    (!filterValues.first_name || row.first_name?.includes(filterValues.first_name)) &&
    (!filterValues.last_name || row.last_name?.includes(filterValues.last_name)) &&
    (!filterValues.department || getEmployeeTypeName(row.employee_type).includes(filterValues.department))
  );

  const handleFilterChange = (field, value) => {
    setFilterValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddStaff = () => {
    navigate('/staff/create');
  };

  const handleExportExcel = () => {
    const columns = [
      { header: 'رقم الهوية', key: 'employee_id' },
      { header: 'الاسم الأول', key: 'first_name' },
      { header: 'اسم العائلة', key: 'last_name' },
      { header: 'رقم الهاتف', key: 'phone_number' },
      { header: 'نوع الموظف', key: 'employee_type_name' },
      { header: 'المدفوع له', key: 'total_paid_this_month' },
      { header: 'إجمالي الراتب', key: 'base_salary' },
    ];
    const data = filteredStaff.map(s => ({
      employee_id: s.employee_id || s.id_number || '',
      first_name: s.first_name,
      last_name: s.last_name,
      phone_number: s.phone_number,
      employee_type_name: getEmployeeTypeName(s.employee_type),
      total_paid_this_month: s.total_paid_this_month || s.paid || 0,
      base_salary: s.base_salary || s.salary || 0,
    }));
    exportToExcel(data, columns, 'staff');
  };

  // زر طباعة تفاصيل موظف (مثال: أول موظف في القائمة)
  const handlePrint = () => {
    const columns = [
      { header: 'رقم الهوية', key: 'employee_id' },
      { header: 'الاسم الأول', key: 'first_name' },
      { header: 'اسم العائلة', key: 'last_name' },
      { header: 'رقم الهاتف', key: 'phone_number' },
      { header: 'نوع الموظف', key: 'employee_type_name' },
      { header: 'المدفوع له', key: 'total_paid_this_month' },
      { header: 'إجمالي الراتب', key: 'base_salary' },
    ];
    const data = filteredStaff.map(s => ({
      employee_id: s.employee_id || s.id_number || '',
      first_name: s.first_name,
      last_name: s.last_name,
      phone_number: s.phone_number,
      employee_type_name: getEmployeeTypeName(s.employee_type),
      total_paid_this_month: s.total_paid_this_month || s.paid || 0,
      base_salary: s.base_salary || s.salary || 0,
    }));
    exportToPrintWindow(data, columns, 'قائمة الموظفين');
  };

  const handleEdit = (id) => {
    navigate(`/staff/edit/${id}`);
  };

  const handleViewFinance = (id) => {
    navigate(`/staff/finance/${id}`);
  };

  const handleDeleteClick = (id) => {
    setSelectedStaffId(id);
    setOpenDialog(true);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteStaff(selectedStaffId));
    setOpenDialog(false);
  };

  const handleCancelDelete = () => {
    setOpenDialog(false);
  };

  const handleArchive = async (id, isCurrentlyArchived = false) => {
    try {
      await dispatch(archiveStaff({ id, is_archived: !isCurrentlyArchived })).unwrap();
      showToast(isCurrentlyArchived ? 'تم استرجاع الموظف بنجاح' : 'تمت أرشفة الموظف بنجاح', 'success');
    } catch (error) {
      console.error('Archiving failed:', error);
      showToast('حدث خطأ أثناء الأرشفة', 'error');
    }
  };

  const getEmployeeTypeName = (id) => {
    if (!id || !employeeTypes?.length) return '';
    const match = employeeTypes.find(t => t.id === id);
    return match?.display_value || '';
  };
  const columns = [
    {
      field: 'employee_id',
      headerName: 'رقم الهوية',
      flex: 1,
      minWidth: 80,
      maxWidth: 200,
      renderCell: (params) => <Typography sx={{ fontSize: 'clamp(0.85rem, 1.1vw, 1.1rem)' }}>{params.row.employee_id || params.row.id_number || '---'}</Typography>,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold" sx={{ fontSize: 'clamp(0.9rem, 1.2vw, 1.2rem)' }}>رقم الهوية</Typography>
          <TextField
            variant="standard"
            value={filterValues.employee_id || ''}
            onChange={(e) => handleFilterChange('employee_id', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{ disableUnderline: true, style: { fontSize: 'clamp(0.8rem, 1vw, 1rem)' } }}
            sx={{ width: '90%', maxWidth: '140px' }}
          />
        </Box>
      )
    },
    {
      field: 'first_name',
      headerName: 'الاسم الأول',
      flex: 1,
      minWidth: 80,
      maxWidth: 200,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold" sx={{ fontSize: 'clamp(0.9rem, 1.2vw, 1.2rem)' }}>الاسم الأول</Typography>
          <TextField
            variant="standard"
            value={filterValues.first_name}
            onChange={(e) => handleFilterChange('first_name', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{ disableUnderline: true, style: { fontSize: 'clamp(0.8rem, 1vw, 1rem)' } }}
            sx={{ width: '90%', maxWidth: '140px' }}
          />
        </Box>
      )
    },
    {
      field: 'last_name',
      headerName: 'اسم العائلة',
      flex: 1,
      minWidth: 80,
      maxWidth: 200,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold" sx={{ fontSize: 'clamp(0.9rem, 1.2vw, 1.2rem)' }}>اسم العائلة</Typography>
          <TextField
            variant="standard"
            value={filterValues.last_name}
            onChange={(e) => handleFilterChange('last_name', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{ disableUnderline: true, style: { fontSize: 'clamp(0.8rem, 1vw, 1rem)' } }}
            sx={{ width: '90%', maxWidth: '140px' }}
          />
        </Box>
      )
    },
    {
      field: 'phone_number',
      headerName: 'رقم الهاتف',
      flex: 1,
      minWidth: 80,
      maxWidth: 200,
      renderCell: (params) => <Typography sx={{ fontSize: 'clamp(0.85rem, 1.1vw, 1.1rem)' }}>{params.row.phone_number || '---'}</Typography>,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold" sx={{ fontSize: 'clamp(0.9rem, 1.2vw, 1.2rem)' }}>رقم الهاتف</Typography>
          <TextField
            variant="standard"
            value={filterValues.phone_number || ''}
            onChange={(e) => handleFilterChange('phone_number', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{ disableUnderline: true, style: { fontSize: 'clamp(0.8rem, 1vw, 1rem)' } }}
            sx={{ width: '90%', maxWidth: '140px' }}
          />
        </Box>
      )
    },
    {
      field: 'employee_type',
      headerName: 'نوع الموظف',
      flex: 1,
      minWidth: 80,
      maxWidth: 200,
      renderCell: (params) => {
        const employeeTypeName = getEmployeeTypeName(params.row.employee_type);
        return <Typography sx={{ fontSize: 'clamp(0.85rem, 1.1vw, 1.1rem)' }}>{employeeTypeName}</Typography>;
      },
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold" sx={{ fontSize: 'clamp(0.9rem, 1.2vw, 1.2rem)' }}>نوع الموظف</Typography>
          <TextField
            variant="standard"
            value={filterValues.department}
            onChange={(e) => handleFilterChange('department', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{ disableUnderline: true, style: { fontSize: 'clamp(0.8rem, 1vw, 1rem)' } }}
            sx={{ width: '90%', maxWidth: '140px' }}
          />
        </Box>
      )
    },
    {
      field: 'total_paid_this_month',
      headerName: 'المدفوع له',
      flex: 1,
      minWidth: 80,
      maxWidth: 200,
      renderCell: (params) => <Typography color="success.main" sx={{ fontSize: 'clamp(0.85rem, 1.1vw, 1.1rem)' }}>{params.row.total_paid_this_month || params.row.paid || 0} شيكل</Typography>,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold" sx={{ fontSize: 'clamp(0.9rem, 1.2vw, 1.2rem)' }}>المدفوع له</Typography>
          <TextField
            variant="standard"
            placeholder="بحث"
            size="small"
            disabled
            InputProps={{ disableUnderline: true, style: { fontSize: 'clamp(0.8rem, 1vw, 1rem)' } }}
            sx={{ width: '90%', maxWidth: '140px', visibility: 'hidden' }}
          />
        </Box>
      )
    },
    {
      field: 'base_salary',
      headerName: 'إجمالي الراتب',
      flex: 1,
      minWidth: 80,
      maxWidth: 200,
      renderCell: (params) => <Typography color="primary.main" sx={{ fontSize: 'clamp(0.85rem, 1.1vw, 1.1rem)' }}>{params.row.base_salary || params.row.salary || 0} شيكل</Typography>,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold" sx={{ fontSize: 'clamp(0.9rem, 1.2vw, 1.2rem)' }}>إجمالي الراتب</Typography>
          <TextField
            variant="standard"
            placeholder="بحث"
            size="small"
            disabled
            InputProps={{ disableUnderline: true, style: { fontSize: 'clamp(0.8rem, 1vw, 1rem)' } }}
            sx={{ width: '90%', maxWidth: '140px', visibility: 'hidden' }}
          />
        </Box>
      )
    },
    {
      field: 'outstanding_balance_current_month',
      headerName: 'الصافي المستحق',
      flex: 1,
      minWidth: 80,
      maxWidth: 200,
      renderCell: (params) => {
        const outstanding = Number(params.row.outstanding_balance_current_month ?? params.row.base_salary ?? 0);
        return (
          <Typography sx={{ color: outstanding >= 0 ? '#0284C7' : '#DC2626', fontWeight: 700, fontSize: 'clamp(0.85rem, 1.1vw, 1.1rem)' }}>
            {outstanding.toLocaleString('en-US')} شيكل
          </Typography>
        );
      },
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold" sx={{ fontSize: 'clamp(0.9rem, 1.2vw, 1.2rem)' }}>الصافي المستحق</Typography>
          <TextField
            variant="standard"
            placeholder="بحث"
            size="small"
            disabled
            InputProps={{ disableUnderline: true, style: { fontSize: 'clamp(0.8rem, 1vw, 1rem)' } }}
            sx={{ width: '90%', maxWidth: '140px', visibility: 'hidden' }}
          />
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'الإجراءات',
      flex: 1.5,
      minWidth: 120,
      maxWidth: 300,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Stack useFlexGap direction="row" spacing={0.5} justifyContent="center" width="100%" flexWrap="wrap">
          {!isArchivedView && (
            <>
              <Tooltip title="تفاصيل الرواتب">
                <IconButton
                  size="small"
                  onClick={() => handleViewFinance(params.row.id)}
                  sx={{
                    color: '#16A34A',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#DCFCE7', color: '#16A34A' },
                  }}
                >
                  <AttachMoneyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="تعديل الموظف">
                <IconButton
                  size="small"
                  onClick={() => handleEdit(params.row.id)}
                  sx={{
                    color: '#D97706',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#FEF3C7', color: '#D97706' },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="أرشفة الموظف">
                <IconButton
                  size="small"
                  onClick={() => handleArchive(params.row.id, false)}
                  sx={{
                    color: '#DC2626',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#FEE2E2', color: '#DC2626' },
                  }}
                >
                  <ArchiveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {isArchivedView && (
            <Tooltip title="استرجاع الموظف">
              <IconButton
                size="small"
                onClick={() => handleArchive(params.row.id, true)}
                sx={{
                  color: '#4338CA',
                  transition: 'all 0.2s',
                  '&:hover': { bgcolor: '#EEF2FF', color: '#4338CA' },
                }}
              >
                <UnarchiveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )
    }
  ];

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        minHeight: '100vh',
        bgcolor: '#F8F9FF',
        overflowX: 'auto',
        maxWidth: '100vw'
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PeopleIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                {isArchivedView ? 'الموظفين المؤرشفين' : 'إدارة الموظفين'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                {isArchivedView ? 'عرض واسترجاع الموظفين المؤرشفين' : 'إدارة وتصدير بيانات الموظفين والرواتب'}
              </Typography>
            </Box>
          </Stack>
          {!isArchivedView && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddStaff}
              sx={{
                bgcolor: '#fff',
                color: '#4338CA',
                px: 3,
                py: 1.1,
                borderRadius: 2.5,
                boxShadow: 'none',
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
              }}
            >
              إضافة موظف جديد
            </Button>
          )}
        </MotionPaper>

        {/* Export Actions */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: '#1E1B4B',
              fontFamily: 'Cairo, Tajawal, sans-serif',
            }}
          >
            تصدير القائمة
          </Typography>
          <Stack useFlexGap
            direction="row"
            spacing={1.5}
            sx={{ flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' }, alignItems: 'center', gap: 1.5 }}
          >
            <Button
              variant="outlined"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                gap: 1,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#16A34A',
                color: '#16A34A',
                '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
              }}
            >
              تصدير Excel
              <Chip
                label={`${filteredStaff.length} موظف`}
                size="small"
                sx={{
                  bgcolor: '#DCFCE7',
                  color: '#16A34A',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  height: 20,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              />
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlePrint}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                gap: 1,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#4338CA',
                color: '#4338CA',
                '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
              }}
            >
              طباعة
              <Chip
                label={`${filteredStaff.length} موظف`}
                size="small"
                sx={{
                  bgcolor: '#EEF2FF',
                  color: '#4338CA',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  height: 20,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              />
            </Button>
          </Stack>
        </MotionPaper>

        {/* DataGrid Card */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
          sx={{
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            bgcolor: '#fff',
            border: '1px solid #E5E7EB',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
          }}
        >
        <DynamicDataGrid
          columns={columns}
          rows={filteredStaff}
          sx={{
            direction: 'rtl',
            fontFamily: 'Alexandria, Arial, sans-serif',
            minWidth: 320,
            maxWidth: '100vw',
            overflowX: 'auto',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f0f0f0',
              fontWeight: 'bold',
              fontSize: 'clamp(0.95rem, 1.2vw, 1.2rem)',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'clamp(0.9rem, 1vw, 1.1rem)',
              padding: 'clamp(6px, 1vw, 12px)',
              textAlign: 'center',
              minWidth: 80,
              maxWidth: '100vw',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            },
            '& .MuiDataGrid-row': {
              height: 'clamp(48px, 6vw, 60px)',
            },
            '& .even': {
              backgroundColor: '#ffffff',
            },
            '& .odd': {
              backgroundColor: '#f9f9f9',
            },
          }}
          getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
          paginationProps={{
            labelRowsPerPage: 'عدد الصفوف في الصفحة:',
            labelDisplayedRows: ({ from, to, count }) =>
              `عرض ${from}–${to} من ${count !== -1 ? count : `أكثر من ${to}`}`,
            nextIconButtonProps: { 'aria-label': 'الصفحة التالية' },
            backIconButtonProps: { 'aria-label': 'الصفحة السابقة' },
            getItemAriaLabel: (type) => {
              if (type === 'first') return 'الصفحة الأولى';
              if (type === 'last') return 'الصفحة الأخيرة';
              if (type === 'next') return 'الصفحة التالية';
              if (type === 'previous') return 'الصفحة السابقة';
              return '';
            },
            dir: 'rtl',
            sx: {
              direction: 'rtl',
              '.MuiTablePagination-toolbar': {
                flexDirection: 'row-reverse',
                justifyContent: 'flex-start',
              },
              '.MuiTablePagination-actions': {
                marginLeft: 0,
                marginRight: '16px',
              },
            }
          }}
        />
        </MotionPaper>
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCancelDelete}
        dir="rtl"
        PaperProps={{
          sx: {
            borderRadius: 3,
            fontFamily: 'Cairo, Tajawal, sans-serif',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: '#4338CA',
            fontFamily: 'Cairo, Tajawal, sans-serif',
          }}
        >
          تأكيد الحذف
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}
          >
            هل أنت متأكد أنك تريد حذف هذا الموظف؟ لا يمكن التراجع عن هذه العملية.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            sx={{
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              borderColor: '#E5E7EB',
              color: '#6B7280',
              '&:hover': { borderColor: '#6B7280', bgcolor: '#F8F9FF' },
            }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="outlined"
            color="error"
            sx={{
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              borderColor: '#DC2626',
              color: '#DC2626',
              '&:hover': { borderColor: '#DC2626', bgcolor: '#FEE2E2' },
            }}
          >
            تأكيد الحذف
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffPage;
